import type { IEventBus } from "@/domains/events"
import { ManagerModeChangedEvent } from "@/domains/manager/events"
import { NftSelectionChangedEvent } from "@/domains/selected-power/events"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import type { StrategyManagerStore } from "./strategy-manager-store"

/**
 * StrategyManagerOrchestrator - Event handler for strategy manager
 *
 * Subscribes to events and delegates to StrategyManagerStore:
 * - Wallet connect/disconnect → Clear all strategies
 * - NFT selection changed → Recalculate all strategies
 */
export class StrategyManagerOrchestrator {
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private managerModeChangedHandler: ((event: ManagerModeChangedEvent) => void) | undefined
  private nftSelectionChangedHandler: ((event: NftSelectionChangedEvent) => void) | undefined

  constructor(
    private readonly strategyManagerStore: StrategyManagerStore,
    private readonly eventBus: IEventBus,
  ) {}

  initialize(): void {
    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    // Subscribe to manager mode changes
    this.managerModeChangedHandler = this.handleManagerModeChanged.bind(this)
    this.eventBus.subscribe(ManagerModeChangedEvent.TYPE, this.managerModeChangedHandler)

    // Subscribe to NFT selection events
    this.nftSelectionChangedHandler = this.handleNftSelectionChanged.bind(this)
    this.eventBus.subscribe(NftSelectionChangedEvent.TYPE, this.nftSelectionChangedHandler)
  }

  private handleWalletConnect(_event: WalletConnectedEvent): void {
    // Clear all strategies when wallet connects
    this.strategyManagerStore.clearAllStrategies()
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    // Clear all strategies when wallet disconnects
    this.strategyManagerStore.clearAllStrategies()
  }

  private handleManagerModeChanged(_event: ManagerModeChangedEvent): void {
    // Clear all strategies when manager mode changes (switching between wallet and manager mode)
    this.strategyManagerStore.clearAllStrategies()
  }

  private handleNftSelectionChanged(event: NftSelectionChangedEvent): void {
    // If no NFTs selected, clear cache
    if (event.selectedNftIds.length === 0) {
      this.strategyManagerStore.clearAllStrategies()
      return
    }

    // Recalculate all strategies with the new selection
    this.strategyManagerStore.recalculateAllStrategies()
  }
}
